import type { UtilisationFilters, FilterOptions } from './types';
import { apiFetch } from '@/services/apiClient';
import { API_URL } from '@/lib/utils';

export type { UtilisationFilters, FilterOptions };

// Default range = last 3 months ending at max_month (clamped to min_month).
export function getDefaultDateRange(
  min_month: string | null,
  max_month: string | null,
): { date_from: string | null; date_to: string | null } {
  if (!min_month || !max_month) return { date_from: null, date_to: null };

  const [maxY, maxM] = max_month.split("T")[0].split("-").map(Number);
  const [minY, minM] = min_month.split("T")[0].split("-").map(Number);

  // Compute "2 months earlier" (3-month window inclusive of max_month)
  let fromY = maxY;
  let fromM = maxM - 2;
  while (fromM <= 0) {
    fromM += 12;
    fromY -= 1;
  }

  // Clamp to min_month
  const fromIdx = fromY * 12 + fromM;
  const minIdx = minY * 12 + minM;
  if (fromIdx < minIdx) {
    fromY = minY;
    fromM = minM;
  }

  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date_from: `${fromY}-${pad(fromM)}-01T00:00:00`,
    date_to: `${maxY}-${pad(maxM)}-01T00:00:00`,
  };
}

export function buildQueryString(filters: UtilisationFilters): string {
  const params = new URLSearchParams();
  filters.legacy_location.forEach(v => params.append('legacy_location', v));
  filters.employees.forEach(v => params.append('employees', v));
  filters.emp_categories.forEach(v => params.append('emp_categories', v));
  filters.service_lines.forEach(v => params.append('service_lines', v));
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchFilterOptions(filters?: UtilisationFilters, signal?: AbortSignal): Promise<Omit<FilterOptions, 'min_month' | 'max_month'>> {
  const response = await apiFetch(`${API_URL}/utilisation/filter-options${filters ? buildQueryString(filters) : ''}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface DateRangeResponse {
  min_month: string | null;
  max_month: string | null;
  available_months?: string[];
}

export async function fetchDateRange(): Promise<DateRangeResponse> {
  const response = await apiFetch(`${API_URL}/utilisation/date-range`, { method: 'GET' });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface MonthlyUtilisationItem {
  monthLabel: string;
  chargeable_hours: number;
  util_pct: number;
  planned_chargeable_hours?: number;
  forecast_util_pct?: number;
}

export interface MonthlyUtilisationResponse {
  util_col?: string;
  data: MonthlyUtilisationItem[];
}

export async function fetchMonthlyUtilisation(filters?: UtilisationFilters, signal?: AbortSignal): Promise<MonthlyUtilisationResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/monthly${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface MonthlySeriesRow {
  month_label: string;
  [series: string]: number | string | null;
}

export interface UtilisationByLocationResponse {
  data: MonthlySeriesRow[];
  series: string[];
}

export async function fetchUtilisationByLocation(filters?: UtilisationFilters, signal?: AbortSignal): Promise<UtilisationByLocationResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/monthly-by-location${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface BurnoutRiskItem {
  monthLabel: string;
  at_risk_pct: number | null;
}

export interface BurnoutRiskResponse {
  data: BurnoutRiskItem[];
}

export async function fetchBurnoutRisk(filters?: UtilisationFilters, signal?: AbortSignal): Promise<BurnoutRiskResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/burnout-risk${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface AvgCorItem {
  monthLabel: string;
  avg_cor: number | null;
}

export interface AvgCorResponse {
  data: AvgCorItem[];
}

export async function fetchAvgCor(filters?: UtilisationFilters, signal?: AbortSignal): Promise<AvgCorResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/avg-cor${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface NonChargeableGbpItem {
  monthLabel: string;
  non_chargeable_gbp: number;
}

export interface NonChargeableGbpResponse {
  data: NonChargeableGbpItem[];
}

export async function fetchNonChargeableGbp(filters?: UtilisationFilters, signal?: AbortSignal): Promise<NonChargeableGbpResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/non-chargeable-gbp-monthly${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface NonChargeableHoursByActivityResponse {
  data: MonthlySeriesRow[];
  series: string[];
}

export async function fetchNonChargeableHoursByActivity(filters?: UtilisationFilters, signal?: AbortSignal): Promise<NonChargeableHoursByActivityResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/non-chargeable-hours-by-activity${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface NonChargeableGbpByServiceResponse {
  data: MonthlySeriesRow[];
  series: string[];
}

export async function fetchNonChargeableGbpByService(filters?: UtilisationFilters, signal?: AbortSignal): Promise<NonChargeableGbpByServiceResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/non-chargeable-gbp-by-service${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export interface UtilisationByGradeItem {
  grade: string;
  util_pct: number | null;
  forecast_util_pct?: number | null;
}

export interface UtilisationByGradeResponse {
  data: UtilisationByGradeItem[];
  average_util_pct: number | null;
  forecast_average_util_pct?: number | null;
}

export async function fetchUtilisationByGrade(filters?: UtilisationFilters, signal?: AbortSignal): Promise<UtilisationByGradeResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  const response = await apiFetch(`${API_URL}/utilisation/utilisation-by-grade${qs}`, { method: 'GET', signal });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}
